/**
 * Error codes for the SDK
 */
export type ErrorCode =
  // Connection errors
  | 'CONNECTION_ERROR'
  | 'CONNECTION_TIMEOUT'
  | 'WEBSOCKET_ERROR'
  | 'NOT_CONNECTED'
  // Device errors
  | 'DEVICE_ERROR'
  | 'DEVICE_NOT_FOUND'
  | 'DEVICE_BUSY'
  | 'DEVICE_DISCONNECTED'
  // Capture errors
  | 'CAPTURE_ERROR'
  | 'CAPTURE_TIMEOUT'
  | 'CAPTURE_CANCELLED'
  | 'QUALITY_ERROR'
  // Protocol errors
  | 'PROTOCOL_ERROR'
  | 'INVALID_RESPONSE'
  | 'COMMAND_TIMEOUT'
  | 'INVALID_REQUEST'
  // Validation errors
  | 'VALIDATION_ERROR'
  // Configuration errors
  | 'CONFIGURATION_ERROR'
  // Internal errors
  | 'INTERNAL_ERROR';

/**
 * Error JSON representation
 */
export interface ErrorJSON {
  name: string;
  code: ErrorCode;
  message: string;
  retryable: boolean;
  timestamp: string;
  context?: Record<string, unknown>;
  stack?: string;
}

/**
 * Base error class for all SDK errors
 */
export abstract class BridgeError extends Error {
  /** Error code */
  abstract readonly code: ErrorCode;

  /** Whether this error can be retried */
  abstract readonly retryable: boolean;

  /** Error timestamp */
  readonly timestamp: Date;

  /** Additional context */
  readonly context?: Record<string, unknown>;

  constructor(message: string, context?: Record<string, unknown>) {
    super(message);
    this.name = this.constructor.name;
    this.timestamp = new Date();
    this.context = context;

    // Maintains proper stack trace (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Convert to JSON representation
   */
  toJSON(): ErrorJSON {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      retryable: this.retryable,
      timestamp: this.timestamp.toISOString(),
      context: this.context,
      stack: this.stack,
    };
  }

  /**
   * Create string representation
   */
  toString(): string {
    return `[${this.code}] ${this.message}`;
  }
}

// ============================================================================
// CONNECTION ERRORS
// ============================================================================

/**
 * Base connection error
 */
export class ConnectionError extends BridgeError {
  readonly code = 'CONNECTION_ERROR' as const;
  readonly retryable = true;
}

/**
 * Connection timeout error
 */
export class ConnectionTimeoutError extends BridgeError {
  readonly code = 'CONNECTION_TIMEOUT' as const;
  readonly retryable = true;

  constructor(timeoutMs: number) {
    super(`Connection timed out after ${timeoutMs}ms`);
  }
}

/**
 * WebSocket error
 */
export class WebSocketError extends BridgeError {
  readonly code = 'WEBSOCKET_ERROR' as const;
  readonly retryable = true;

  constructor(message: string, public readonly originalError?: Error) {
    super(message, { originalError: originalError?.message });
  }
}

/**
 * Not connected error
 */
export class NotConnectedError extends BridgeError {
  readonly code = 'NOT_CONNECTED' as const;
  readonly retryable = false;

  constructor() {
    super('Not connected to Iron Bridge');
  }
}

// ============================================================================
// DEVICE ERRORS
// ============================================================================

/**
 * Base device error
 */
export class DeviceError extends BridgeError {
  readonly code = 'DEVICE_ERROR' as const;
  readonly retryable = true;
  readonly deviceType?: string;

  constructor(message: string, deviceType?: string, context?: Record<string, unknown>) {
    super(message, { ...context, deviceType });
    this.deviceType = deviceType;
  }
}

/**
 * Device not found error
 */
export class DeviceNotFoundError extends BridgeError {
  readonly code = 'DEVICE_NOT_FOUND' as const;
  readonly retryable = true;

  constructor(deviceType?: string) {
    super(deviceType ? `Device not found: ${deviceType}` : 'No device found');
  }
}

/**
 * Device busy error
 */
export class DeviceBusyError extends BridgeError {
  readonly code = 'DEVICE_BUSY' as const;
  readonly retryable = true;

  constructor(deviceType?: string) {
    super(deviceType ? `Device busy: ${deviceType}` : 'Device is busy');
  }
}

/**
 * Device disconnected error
 */
export class DeviceDisconnectedError extends BridgeError {
  readonly code = 'DEVICE_DISCONNECTED' as const;
  readonly retryable = true;

  constructor(deviceType?: string) {
    super(deviceType ? `Device disconnected: ${deviceType}` : 'Device disconnected');
  }
}

// ============================================================================
// CAPTURE ERRORS
// ============================================================================

/**
 * Base capture error
 */
export class CaptureError extends BridgeError {
  readonly code = 'CAPTURE_ERROR' as const;
  readonly retryable = false;
}

/**
 * Capture timeout error
 */
export class CaptureTimeoutError extends BridgeError {
  readonly code = 'CAPTURE_TIMEOUT' as const;
  readonly retryable = true;

  constructor(timeoutMs: number) {
    super(`Capture timed out after ${timeoutMs}ms`);
  }
}

/**
 * Capture cancelled error
 */
export class CaptureCancelledError extends BridgeError {
  readonly code = 'CAPTURE_CANCELLED' as const;
  readonly retryable = false;

  constructor() {
    super('Capture was cancelled');
  }
}

/**
 * Quality threshold error
 */
export class QualityError extends BridgeError {
  readonly code = 'QUALITY_ERROR' as const;
  readonly retryable = false;
  readonly qualityScore: number;
  readonly minimumRequired: number;

  constructor(qualityScore: number, minimumRequired: number) {
    super(`Quality score ${qualityScore} below minimum ${minimumRequired}`);
    this.qualityScore = qualityScore;
    this.minimumRequired = minimumRequired;
  }
}

// ============================================================================
// PROTOCOL ERRORS
// ============================================================================

/**
 * Base protocol error
 */
export class ProtocolError extends BridgeError {
  readonly code = 'PROTOCOL_ERROR' as const;
  readonly retryable = false;
}

/**
 * Invalid response error
 */
export class InvalidResponseError extends BridgeError {
  readonly code = 'INVALID_RESPONSE' as const;
  readonly retryable = false;

  constructor(message: string, public readonly rawResponse?: unknown) {
    super(message, { rawResponse });
  }
}

/**
 * Command timeout error
 */
export class CommandTimeoutError extends BridgeError {
  readonly code = 'COMMAND_TIMEOUT' as const;
  readonly retryable = true;
  readonly action: string;

  constructor(action: string, timeoutMs: number) {
    super(`Command '${action}' timed out after ${timeoutMs}ms`);
    this.action = action;
  }
}

/**
 * Invalid request error
 */
export class InvalidRequestError extends BridgeError {
  readonly code = 'INVALID_REQUEST' as const;
  readonly retryable = false;

  constructor(message: string, public readonly field?: string) {
    super(message, { field });
  }
}

// ============================================================================
// VALIDATION ERRORS
// ============================================================================

/**
 * Validation error
 */
export class ValidationError extends BridgeError {
  readonly code = 'VALIDATION_ERROR' as const;
  readonly retryable = false;
  readonly field?: string;

  constructor(message: string, field?: string) {
    super(message, { field });
    this.field = field;
  }
}

// ============================================================================
// CONFIGURATION ERRORS
// ============================================================================

/**
 * Configuration error
 */
export class ConfigurationError extends BridgeError {
  readonly code = 'CONFIGURATION_ERROR' as const;
  readonly retryable = false;

  constructor(message: string, public readonly configKey?: string) {
    super(message, { configKey });
  }
}

// ============================================================================
// INTERNAL ERRORS
// ============================================================================

/**
 * Internal error
 */
export class InternalError extends BridgeError {
  readonly code = 'INTERNAL_ERROR' as const;
  readonly retryable = false;

  constructor(message: string, public readonly originalError?: Error) {
    super(message, { originalError: originalError?.message });
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if an error is a BridgeError
 */
export function isBridgeError(error: unknown): error is BridgeError {
  return error instanceof BridgeError;
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (isBridgeError(error)) {
    return error.retryable;
  }
  return false;
}

/**
 * Create a BridgeError from an unknown error
 */
export function toBridgeError(error: unknown): BridgeError {
  if (isBridgeError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return new InternalError(error.message, error);
  }

  return new InternalError(String(error));
}
